import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

export const sunlightOptions = {
  fullSun: 'Full Sun',
  partShade: 'Full Sun to Part Shade',
  fullShade: 'Full Shade',
};

export const maintenanceOptions = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const growthRateOptions = {
  slow: 'Slow',
  moderate: 'Moderate',
  rapid: 'Rapid',
};

export const typeOptions = {
  groundcover: 'Groundcover',
  grass: 'Grass',
  evergreen: 'Evergreen',
  // tropical: 'Tropical', // Commenting out until we have tropical plants to sell
  flowering: 'Flowering',
  fruit: 'Fruit',
  edible: 'Edible',
  privacy: 'Privacy',
  shade: 'Shade',
};

export const characteristicOptions = {
  deerResistant: 'Deer resistant',
  petSafe: 'Pet safe',
  fragrant: 'Fragrant',
  containerFriendly: 'Container friendly',
  pollinatorFriendly: 'Pollinator friendly',
  wildlifeFriendly: 'Wildlife friendly',
};

export const matureHeightOptions = {
  lessThan3: {
    minValue: 0,
    maxValue: 2.99,
    label: 'Less than 3 ft',
  },
  '3to4': {
    minValue: 3,
    maxValue: 4.99,
    label: '3 ft to 4 ft',
  },
  '5to8': {
    minValue: 5,
    maxValue: 8.99,
    label: '5 ft to 8 ft',
  },
  '9to12': {
    minValue: 9,
    maxValue: 12.99,
    label: '9 ft to 12 ft',
  },
  '13to20': {
    minValue: 13,
    maxValue: 20.99,
    label: '13 ft to 20 ft',
  },
  greaterThan20: {
    minValue: 21,
    maxValue: 200,
    label: 'Greater than 20 ft',
  },
};

export const matureWidthOptions = {
  lessThan3: {
    minValue: 0,
    maxValue: 3,
    label: 'Less than 3ft',
  },
  '3to5': {
    minValue: 3,
    maxValue: 5,
    label: '3ft to 5ft',
  },
  '5to8': {
    minValue: 5,
    maxValue: 8,
    label: '5ft to 8ft',
  },
  '8to12': {
    minValue: 8,
    maxValue: 12,
    label: '8ft to 12ft',
  },
  greaterThan12: {
    minValue: 12,
    maxValue: 400,
    label: 'Greater than 12ft',
  },
};

export const hardinessZoneOptions = {
  2: 'Zone 2',
  3: 'Zone 3',
  4: 'Zone 4',
  5: 'Zone 5',
  6: 'Zone 6',
  7: 'Zone 7',
  8: 'Zone 8',
  9: 'Zone 9',
  10: 'Zone 10',
  11: 'Zone 11',
  12: 'Zone 12',
};

export const bloomColorOptions = {
  blue: 'Blue',
  brown: 'Brown',
  green: 'Green',
  orange: 'Orange',
  pink: 'Pink',
  purple: 'Purple',
  red: 'Red',
  white: 'White',
  yellow: 'Yellow',
};

export const foliageColorOptions = {
  black: 'Black',
  green: 'Green',
  orange: 'Orange',
  pink: 'Pink',
  purple: 'Purple',
  red: 'Red',
  white: 'White',
  yellow: 'Yellow',
};

export const bloomSeasonOptions = {
  spring: 'Spring',
  summer: 'Summer',
  fall: 'Fall',
  winter: 'Winter',
};

const DEFAULT_ACCESSOR = (product) => {
  return product.productDetails.category.name;
};

export const useFilter = (
  items,
  { paramName = 'category', accessor = DEFAULT_ACCESSOR } = {}
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const filterValue = params.get(paramName) || '';

  const filteredItems = items
    ? items.filter((item) => !filterValue || accessor(item) === filterValue)
    : [];

  return {
    filterValue,
    filteredItems,
    setFilter: (newFilterValue) => {
      params.delete('page');
      if (newFilterValue) {
        params.set(paramName, newFilterValue);
      } else {
        params.delete(paramName);
      }
      navigate(`?${params.toString()}`);
    },
  };
};

export const useFiltering = (items = [], options) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const filterValues = {};
  Object.keys(options).forEach((paramName) => {
    filterValues[paramName] = params.getAll(paramName);
  });

  const filteredItems = filterForParams(items, options, params);

  const setFilter = useCallback(
    (
      paramKey,
      paramValue,
      { onRemove, onAdd, replace } = {
        onRemove: () => {},
        onAdd: () => {},
        replace: false, // by default, we allow multiple params for a single key, use `replace = true` to swap param values
      }
    ) => {
      const params = new URLSearchParams(location.search);
      params.delete('page');
      let nextParams = params;
      if (replace) {
        nextParams.delete(paramKey);
        nextParams.set(paramKey, paramValue);
        onAdd && onAdd(nextParams);
        onRemove && onRemove(nextParams);
      } else {
        nextParams = toggleParam(params, paramKey, paramValue, onRemove, onAdd);
      }
      navigate(`?${nextParams}`);
    },
    [location.search, navigate]
  );

  const clearAllFilters = useCallback(() => {
    navigate(location.pathname);
  }, [navigate, location.pathname]);

  const clearFilter = (key, paramsToUpdate) => {
    // `paramsToUpdate` is an optional parameter that's useful when you need to modify
    // a URLSearchParams object outside of this function call (e.g. if `clearFilter` is used)
    // in an `onRemove` callback
    const params = paramsToUpdate || new URLSearchParams(location.search);
    params.delete(key);

    // If we do NOT have a `paramsToUpdate` passed in, we need to navigate after clearing the filter`
    if (!paramsToUpdate) {
      navigate(`?${params}`);
    }
  };

  return {
    filteredItems,
    filterValues,
    setFilter,
    clearAllFilters,
    clearFilter,
  };
};

export function filterForParams(items, options, params) {
  let filteredItems = items;

  Object.entries(options).forEach(([paramName, filterFn]) => {
    filteredItems = filteredItems.filter((item) => {
      const activeFilters = params.getAll(paramName);
      if (activeFilters.length === 0) {
        return true;
      }

      return filterFn(item, activeFilters);
    });
  });

  return filteredItems;
}

export function toggleParam(
  params,
  key,
  value,
  onRemove = () => {},
  onAdd = () => {}
) {
  const existingValues = params.getAll(key);
  const shouldRemoveParamValue = existingValues.includes(value);

  if (shouldRemoveParamValue) {
    // Clear all the values for this key and re-add them one at a time
    params.delete(key);
    const withExistingRemoved = existingValues.filter((v) => v !== value);
    withExistingRemoved.forEach((v) => params.append(key, v));
    onRemove(params);
  } else {
    params.append(key, value);
    onAdd(params);
  }

  return params;
}
